import CustomButton from "@/components/common/Button";
import { DropdownService } from "@/services/APIs";
import { WorkflowState } from "@/utils/types/workflowMain";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Card,
  Col,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { AlertTriangle, Edit2, Info, Plus, Trash } from "lucide-react";

const { Title, Text } = Typography;

interface ValidationError {
  rowIndex: number;
  field: string;
  message: string;
}

interface WorkflowStatesTableProps {
  workflowStates: WorkflowState[];
  validationErrors: ValidationError[];
  onAddState: () => void;
  onEditState: (index: number) => void;
  onRemoveState: (index: number) => void;
  disabled?: boolean;
  isRemovingState?: number | null;
}

const WorkflowStatesTable = ({
  workflowStates,
  validationErrors,
  onAddState,
  onEditState,
  onRemoveState,
  disabled = false,
  isRemovingState = null,
}: WorkflowStatesTableProps) => {
  const { data: stateMasters } = useQuery({
    queryKey: ["workflow_state_master"],
    queryFn: () => DropdownService.getDropdownData("workflow_state_master"),
  });

  const { data: actionMasters } = useQuery({
    queryKey: ["workflow_action"],
    queryFn: () => DropdownService.getDropdownData("workflow_action"),
  });

  const { data: tenantRoles } = useQuery({
    queryKey: ["role"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });

  const { data: tenantUsers } = useQuery({
    queryKey: ["user"],
    queryFn: () => DropdownService.getDropdownData("user"),
  });

  // Helper functions for displaying data
  const getStateMasterById = (id: string) => {
    return stateMasters?.find(
      (state: { value: string; label: string }) => state.value === id
    );
  };

  const getActionMasterById = (id: string) => {
    return actionMasters?.find(
      (action: { value: string; label: string }) => action.value === id
    );
  };

  const getRoleMasterById = (id: string) => {
    return tenantRoles?.find(
      (role: { value: string; label: string }) => role.value === id
    );
  };

  const getUserMasterById = (id: string) => {
    return tenantUsers?.find(
      (user: { value: string; label: string }) => user.value === id
    );
  };

  // Table columns configuration
  const columns = [
    {
      title: "Step",
      dataIndex: "step",
      key: "step",
      width: 60,
      render: (_: any, __: any, index: number) => (
        <Tag color="blue">{index + 1}</Tag>
      ),
      sorter: (a: any, b: any) => a.step!.localeCompare(b.step!),
    },
    {
      title: "Current State",
      dataIndex: "state_id",
      key: "state_id",
      width: 150,
      render: (value: string) => {
        if (!value) {
          return "Not selected";
        }
        const state = getStateMasterById(value);
        return state?.label ?? "Not selected";
      },
    },
    {
      title: "Action",
      dataIndex: "action_id",
      key: "action_id",
      width: 150,
      render: (value: string) => {
        if (!value) {
          return "Not selected";
        }
        const action = getActionMasterById(value);
        return action?.label ?? "Not selected";
      },
    },
    {
      title: "Next State",
      dataIndex: "next_state_id",
      key: "next_state_id",
      width: 150,
      render: (value: string) => {
        if (!value) {
          return "Not selected";
        }
        const nextState = getStateMasterById(value);
        return nextState?.label ?? "Not selected";
      },
    },
    {
      title: "Role",
      dataIndex: "role_id",
      key: "role_id",
      width: 120,
      render: (value: string) => {
        if (!value) {
          return "Not assigned";
        }
        const role = getRoleMasterById(value);
        return role?.label ?? "Not assigned";
      },
    },
    {
      title: "User",
      dataIndex: "user_id",
      key: "user_id",
      width: 150,
      render: (value: string) => {
        if (!value) {
          return "Not assigned";
        }
        const user = getUserMasterById(value);
        return user?.label ?? "Not assigned";
      },
    },
    {
      title: "Due Days",
      dataIndex: "due_day",
      key: "due_day",
      width: 100,
      render: (value: number) => {
        if (!value || value < 1) {
          return "Not set";
        }
        return `${value} days`;
      },
    },
    {
      title: "Condition",
      dataIndex: "condition",
      key: "condition",
      width: 200,
      render: (value: string) => {
        if (!value) {
          return "No condition";
        }
        return (
          <Tooltip title={value}>
            <div className="max-w-[180px] truncate">
              {value.length > 30 ? `${value.substring(0, 30)}...` : value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      key: "actions",
      render: (_: any, __: any, index: number) => (
        <Space>
          <CustomButton
            size="small"
            icon={<Edit2 size={16} />}
            type="text"
            onClick={() => onEditState(index)}
            disabled={disabled}
            title="Edit State Transition"
          />
          <Popconfirm
            title="Are you sure you want to remove this state?"
            onConfirm={() => onRemoveState(index)}
            disabled={disabled}
          >
            <CustomButton
              danger
              size="small"
              icon={<Trash size={16} />}
              type="text"
              loading={isRemovingState === index}
              disabled={disabled}
              title="Remove State Transition"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <span>Workflow States & Transitions</span>
          <Tooltip title="Define the flow of states and actions in your workflow">
            <Info size={16} className="text-gray-400" />
          </Tooltip>
        </Space>
      }
      className="mb-6"
      extra={
        <CustomButton
          type="dashed"
          icon={<Plus size={16} />}
          onClick={onAddState}
          disabled={disabled}
          title="Add New State Transition"
        >
          Add State Transition
        </CustomButton>
      }
    >
      {validationErrors.length > 0 && (
        <Alert
          message={`${validationErrors.length} validation error(s) found`}
          description="Please fix the errors below before saving"
          type="error"
          showIcon
          icon={<AlertTriangle size={16} />}
          className="mb-4"
        />
      )}

      {workflowStates.length === 0 ? (
        <div className="py-12 text-center text-gray-500">
          <div className="mb-4">
            <Info size={48} className="mx-auto text-gray-300" />
          </div>
          <Text className="text-lg">No workflow states defined</Text>
          <br />
          <Text type="secondary">
            {`Click "Add State Transition" to start building your workflow`}
          </Text>
        </div>
      ) : (
        <Table
          dataSource={workflowStates}
          columns={columns}
          pagination={false}
          size="small"
          rowKey={index => index?.toString() ?? "0"}
          scroll={{ x: 1200 }}
          className="workflow-states-table"
        />
      )}

      {workflowStates.length > 0 && (
        <Card className="mt-4">
          <Row gutter={16}>
            <Col span={8}>
              <div className="text-center">
                <Title level={5} style={{ margin: 0 }}>
                  {workflowStates.length}
                </Title>
                <Text type="secondary">Total Transitions</Text>
              </div>
            </Col>
            <Col span={8}>
              <div className="text-center">
                <Title level={5} style={{ margin: 0 }}>
                  {
                    new Set(workflowStates.map(s => s.state_id).filter(Boolean))
                      .size
                  }
                </Title>
                <Text type="secondary">Unique States</Text>
              </div>
            </Col>
            <Col span={8}>
              <div className="text-center">
                <Title level={5} style={{ margin: 0 }}>
                  {
                    new Set(
                      workflowStates.map(s => s.action_id).filter(Boolean)
                    ).size
                  }
                </Title>
                <Text type="secondary">Unique Actions</Text>
              </div>
            </Col>
          </Row>
        </Card>
      )}
    </Card>
  );
};

export default WorkflowStatesTable;
